import FileService from "#src/components/Services/FileService";
import {
  fetchFileDownloadMetaWithScanPolling,
  getImportScanPollConfig,
  isFileScanTerminal,
  type FileScanPollConfigType,
} from "#src/utils/fileDownload";

/** Progress reported while a dataset import upload is in flight. */
export type DatasetFileImportStatusType = {
  percentComplete: number;
  label: string;
};

/** A file registration already made with the file upload service for a given browser `File`. */
export type DatasetFileImportRegistrationType = {
  fileSignature: string;
  fileId: string;
  transactionId: string;
};

export type DatasetFileImportResultType = {
  fileId: string;
  transactionId: string;
};

const getFileSignature = (file: File, datasetId: string): string =>
  `${file.name}::${file.size}::${file.lastModified}::${datasetId}`;

/**
 * Registers a browser `File` with the file upload service, uploads it to the
 * returned pre-signed URL, then polls for its GuardDuty scan result.
 *
 * If `previousRegistration` matches `file` (by name/size/lastModified) **and** `datasetId`, its
 * `fileId`/`transactionId` are reused instead of registering and uploading
 * the file again — this keeps a retry after a scan-poll timeout from
 * creating a second, duplicate transaction for the same upload. Callers
 * should store the registration (e.g. in a `useRef`) via `onRegistered` and
 * pass it back in on retry, clearing it once the import succeeds.
 */
export const uploadFileForDatasetImport = async ({
  file,
  datasetId,
  companyId,
  previousRegistration,
  onStatusChange,
  onRegistered,
  scanPollConfig,
}: {
  file: File;
  datasetId: string;
  companyId?: string;
  previousRegistration?: DatasetFileImportRegistrationType | null;
  onStatusChange?: (status: DatasetFileImportStatusType) => void;
  onRegistered?: (registration: DatasetFileImportRegistrationType) => void;
  /** Overrides the file-size-scaled default from {@link getImportScanPollConfig}. */
  scanPollConfig?: FileScanPollConfigType;
}): Promise<DatasetFileImportResultType> => {
  const fileSignature = getFileSignature(file, datasetId);
  let fileId: string;
  let transactionId: string;

  if (previousRegistration?.fileSignature === fileSignature) {
    fileId = previousRegistration.fileId;
    transactionId = previousRegistration.transactionId;
  } else {
    const { name, type: contentType, lastModified } = file;

    onStatusChange?.({ percentComplete: 1, label: "Uploading..." });

    const {
      status: createFileStatus,
      data: {
        data: [uploadedFileData],
        meta: { transaction_id: createdTransactionId },
      },
    } = await FileService.createFile({
      name,
      contentType,
      description:
        "A user-uploaded file through the import data action component",
      tags: {
        lastModified: new Date(lastModified).toISOString(),
      },
      datasetId,
      companyId,
    })!;

    if (createFileStatus !== 200 && createFileStatus !== 201) {
      throw new Error("Could not create an entry in the file upload service");
    }

    fileId = uploadedFileData.file_id;
    transactionId = createdTransactionId;

    const { status: uploadFileStatus } = await FileService.uploadFile({
      url: uploadedFileData.uri,
      fileBlob: file,
    })!;

    if (uploadFileStatus !== 200 && uploadFileStatus !== 201) {
      throw new Error(
        "Could not upload file to the created entry in file upload service"
      );
    }
  }

  onRegistered?.({ fileSignature, fileId, transactionId });

  onStatusChange?.({ percentComplete: 33, label: "Scanning..." });

  const scannedFileData = await fetchFileDownloadMetaWithScanPolling(fileId, {
    isComplete: isFileScanTerminal,
    ...getImportScanPollConfig(file.size),
    ...scanPollConfig,
  });

  onStatusChange?.({ percentComplete: 66, label: "Verifying..." });

  if (scannedFileData.file_scan_status !== "safe") {
    throw new Error(
      "The uploaded file has been deemed as unsafe or failed to complete verification"
    );
  }

  onStatusChange?.({ percentComplete: 100, label: "Uploading Complete" });

  return { fileId, transactionId };
};
